import { GetPageResponse } from "@store/features/crm/pageV2Slice";
import type { Field } from "skedyul-core/features/crm/features/pages/resources/types";

type UrlFieldLike = {
  definition?: unknown;
};

/** True when the field definition applies URL normalization. */
export function isUrlField(field: UrlFieldLike): boolean {
  const definition = field.definition;
  if (!definition || typeof definition !== "object") {
    return false;
  }

  const normalizations = (
    definition as { normalizations?: Array<{ handle: string }> | null }
  ).normalizations;

  return normalizations?.some((n) => n.handle === "url") ?? false;
}

/** True when a cell value should render as a clickable external link. */
export function looksLikeUrl(value: string): boolean {
  const trimmed = value.trim();
  if (!trimmed) return false;
  if (/^https?:\/\/.+/i.test(trimmed)) return true;
  return false;
}

/** Ensures a URL string has a protocol for use in href attributes. */
export function normalizeUrlHref(value: string): string {
  const trimmed = value.trim();
  if (!trimmed) return "";
  if (/^https?:\/\//i.test(trimmed)) return trimmed;
  return `https://${trimmed}`;
}

export const getByPath = (
  obj: Record<string, unknown>,
  path: string,
): unknown => {
  if (!obj || !path) return undefined;
  const parts = path.split(".");
  let current: unknown = obj;
  for (const part of parts) {
    if (current == null || typeof current !== "object") return undefined;
    current = (current as Record<string, unknown>)[part];
  }
  return current;
};

export const getErrorForRowAndPath = (
  row: Record<string, unknown>,
  accessorPath: string,
  pageData: GetPageResponse | null | undefined,
): { code: string; message: string } | undefined => {
  const rowErrors = (
    row as { _errors?: Record<string, { code: string; message: string }> }
  )._errors;
  if (rowErrors && rowErrors[accessorPath]) return rowErrors[accessorPath];

  if (!pageData || !Array.isArray(pageData.fields)) return undefined;
  const leafHandle = accessorPath.split(".").pop() as string;
  const field = pageData.fields.find((f: Field) => f.handle === leafHandle);
  if (field && field.error)
    return { code: field.error.code, message: field.error.message };
  return undefined;
};
